import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface AuthUser {
  name: string;
  email: string;
  id: string;
  tenant_id?: string;
  document_unique_number?: string;
  associated_owner_id?: string;
  type?: string;
  system_role?: string;
  onboarding_status?: string;
}

export interface AuthStore {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  refreshToken: string | null;
  hasHydrated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  setUser: (user: AuthUser | null) => void;
  setToken: (token: string | null) => void;
  setRefreshToken: (token: string | null) => void;
  setHasHydrated: (value: boolean) => void;
  reset: () => void;
}

export interface AuthStoreConfig {
  storageKey: string;
  version: number;
  validateUser: (user: AuthUser) => boolean;
  logPrefix: string;
}

export function createAuthStore(config: AuthStoreConfig) {
  return create<AuthStore>()(
    persist(
      (set) => ({
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        hasHydrated: false,
        setIsAuthenticated: (value) => set({ isAuthenticated: value }),
        setUser: (user) => set({ user }),
        setToken: (token) => set({ token }),
        setRefreshToken: (token) => set({ refreshToken: token }),
        setHasHydrated: (value) => set({ hasHydrated: value }),
        reset: () => set({
          isAuthenticated: false,
          user: null,
          token: null,
          refreshToken: null,
        }),
      }),
      {
        name: config.storageKey,
        version: config.version,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          isAuthenticated: state.isAuthenticated,
          user: state.user,
          token: state.token,
          refreshToken: state.refreshToken,
          hasHydrated: state.hasHydrated,
        }),
        onRehydrateStorage: () => (state) => {
          if (state?.user && !config.validateUser(state.user)) {
            state?.setIsAuthenticated(false);
            state?.setUser(null);
            state?.setToken(null);
          }

          if (state?.isAuthenticated && !state?.token) {
            console.warn(`[${config.logPrefix}] Inconsistent state: authenticated without token. Clearing session.`);
            state?.setIsAuthenticated(false);
            state?.setUser(null);
          }

          state?.setHasHydrated(true);
        },
      }
    )
  );
}
